import React from "react";
import { useTranslation } from "react-i18next";
import { Select } from "components/select/index";
import type { TBuilderToolbarMenuViewPresentation } from "components/builderShell/BuilderToolbarMenuView";
import { BuilderToolbarMenuView } from "components/builderShell/BuilderToolbarMenuView";
import type { IBuilderToolbarMenuViewSection } from "components/builderShell/types";
import "components/builderShell/builderToolbarMenuView.scss";
import {
  baselineEmailSettings,
  baselinePagePadding,
} from "../../../domain/baselines";
import { selectDocument } from "../../../domain/selectors";
import { useEditorActions, useEditorStore } from "../../../domain/redux/hooks";
import { EMAIL_FONTS } from "../../../domain/theme";
import type { EmailFontFamily } from "../../../domain/types";
import { useEmailBuilderSettingsOverlayPortalId } from "../../canvas/settings/emailBuilderSettingsOverlayPortal";
import { EmailBuilderBoundedRange } from "../blockSettings/EmailBuilderBlockSettingsShared";
import {
  EmailBuilderSettingsInlineField,
} from "../blockSettings/EmailBuilderSettingsLayout";
import { EmailBuilderSettingsPaddingSection } from "../../../settings/controls/EmailBuilderSettingsPaddingSection";
import "../blockSettings/emailBuilderBlockSettings.scss";

const { Option } = Select;

const CONTENT_WIDTH_MIN = 480;
const CONTENT_WIDTH_MAX = 800;

interface IEmailBuilderGeneralToolbarMenuProps {
  presentation?: TBuilderToolbarMenuViewPresentation;
  panelTitle?: string;
  goBackToParentView?: () => void;
  onClose?: () => void;
  closeAriaLabel?: string;
}

function renderFontOptionLabel(font: EmailFontFamily) {
  return <span style={{ fontFamily: font }}>{font.split(",")[0].replace(/'/g, "")}</span>;
}

export function EmailBuilderGeneralToolbarMenu(props: IEmailBuilderGeneralToolbarMenuProps) {
  const { t } = useTranslation();
  const presentation = props.presentation ?? "desktop-panel";
  const overlayPortalId = useEmailBuilderSettingsOverlayPortalId();
  const document = useEditorStore(selectDocument);
  const { updateSettings } = useEditorActions();
  const settings = document.settings;
  const defaults = baselineEmailSettings();
  const pagePadding = settings.pagePadding ?? baselinePagePadding();

  const sections: IBuilderToolbarMenuViewSection[] = [
    {
      title: t("Layout"),
      content: (
        <div className="email-builder-block-settings">
          <EmailBuilderBoundedRange
            label={t("Width")}
            value={settings.contentWidth}
            min={CONTENT_WIDTH_MIN}
            max={CONTENT_WIDTH_MAX}
            defaultValue={defaults.contentWidth}
            onChange={contentWidth => {
              if (contentWidth === undefined) {
                return;
              }
              updateSettings({ contentWidth });
            }}
          />
        </div>
      ),
    },
    {
      title: t("Padding"),
      content: (
        <div className="email-builder-block-settings">
          <EmailBuilderSettingsPaddingSection
            fieldLabel={t("Page padding (px)")}
            value={pagePadding}
            defaultValue={baselinePagePadding()}
            onChange={nextPagePadding => {
              updateSettings({ pagePadding: nextPagePadding });
            }}
          />
        </div>
      ),
    },
    {
      title: t("Typography"),
      content: (
        <div className="email-builder-block-settings">
          <EmailBuilderSettingsInlineField label={t("Heading font")}>
            <Select
              defaultValue={settings.headingFontFamily}
              onChange={(headingFontFamily: EmailFontFamily) => {
                updateSettings({ headingFontFamily });
              }}
              usePortal
              portalId={overlayPortalId}
            >
              {EMAIL_FONTS.map(font => (
                <Option key={font} value={font}>
                  {renderFontOptionLabel(font)}
                </Option>
              ))}
            </Select>
          </EmailBuilderSettingsInlineField>
          <EmailBuilderSettingsInlineField label={t("Body font")}>
            <Select
              defaultValue={settings.bodyFontFamily}
              onChange={(bodyFontFamily: EmailFontFamily) => {
                updateSettings({ bodyFontFamily });
              }}
              usePortal
              portalId={overlayPortalId}
            >
              {EMAIL_FONTS.map(font => (
                <Option key={font} value={font}>
                  {renderFontOptionLabel(font)}
                </Option>
              ))}
            </Select>
          </EmailBuilderSettingsInlineField>
        </div>
      ),
    },
  ];

  return (
    <BuilderToolbarMenuView
      title={props.panelTitle ?? t("General")}
      presentation={presentation}
      goBackToParentView={props.goBackToParentView}
      onClose={props.onClose}
      closeAriaLabel={props.closeAriaLabel}
      sections={sections}
    />
  );
}
